/**
 * useQBranchPersonnelLibrary — 2026-06-08 Phase 5E wire-up for the
 * canonical Q Manual VG35002 Q Branch personnel.
 *
 * Mirrors useGadgetsLibrary.ts pattern.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PersonnelManifestEntry } from '../services/host/QBranchPersonnelServiceModule';
import { QBranchPersonnelServiceModule } from '../services/host/QBranchPersonnelServiceModule';

export type QBranchPersonnelLibraryState =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error';

export type QBranchPersonnelLibraryTab =
  | 'leadership'
  | 'harrow_club'
  | 'newer_member';

export interface QBranchPersonnelLibraryHookResult {
  state: QBranchPersonnelLibraryState;
  personnel: PersonnelManifestEntry[];
  error: string | null;
  refresh: () => void;
  activeTab: QBranchPersonnelLibraryTab;
  setActiveTab: (tab: QBranchPersonnelLibraryTab) => void;
  selectedPersonnelId: string | null;
  selectedPersonnelData: Record<string, unknown> | null;
  selectedPersonnelState: QBranchPersonnelLibraryState;
  selectPersonnel: (id: string | null) => void;
}

export function useQBranchPersonnelLibrary(
  personnelService?: QBranchPersonnelServiceModule,
): QBranchPersonnelLibraryHookResult {
  const resolvedService = useMemo(
    () => personnelService ?? new QBranchPersonnelServiceModule(),
    [personnelService],
  );

  const [state, setState] = useState<QBranchPersonnelLibraryState>('idle');
  const [personnel, setPersonnel] = useState<PersonnelManifestEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const [activeTab, setActiveTab] =
    useState<QBranchPersonnelLibraryTab>('leadership');

  const [selectedPersonnelId, setSelectedPersonnelId] = useState<string | null>(
    null,
  );
  const [selectedPersonnelData, setSelectedPersonnelData] = useState<
    Record<string, unknown> | null
  >(null);
  const [selectedPersonnelState, setSelectedPersonnelState] =
    useState<QBranchPersonnelLibraryState>('idle');

  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  const selectPersonnel = useCallback((id: string | null) => {
    setSelectedPersonnelId(id);
    if (id === null) {
      setSelectedPersonnelData(null);
      setSelectedPersonnelState('idle');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    setState('loading');
    setError(null);

    (async () => {
      try {
        const result = await resolvedService.list();
        if (cancelled) return;
        setPersonnel(result.personnel);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setPersonnel([]);
        setError(e instanceof Error ? e.message : String(e));
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, refreshTick]);

  useEffect(() => {
    if (selectedPersonnelId === null) {
      return;
    }
    let cancelled = false;

    setSelectedPersonnelState('loading');
    setSelectedPersonnelData(null);

    (async () => {
      try {
        const result = await resolvedService.get(selectedPersonnelId);
        if (cancelled) return;
        if (result === null) {
          setSelectedPersonnelState('error');
          return;
        }
        setSelectedPersonnelData(result);
        setSelectedPersonnelState('ready');
      } catch {
        if (cancelled) return;
        setSelectedPersonnelState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, selectedPersonnelId]);

  return {
    state,
    personnel,
    error,
    refresh,
    activeTab,
    setActiveTab,
    selectedPersonnelId,
    selectedPersonnelData,
    selectedPersonnelState,
    selectPersonnel,
  };
}
